import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { ModalModule } from 'ngx-bootstrap';

import { CanActivateViaAuthGuard } from './services/authorization.guard';
import { AuthService } from './services/auth.service';
import { ClientLogService } from './services/client.log.service';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';

import { IdleComponent } from './common/elements/component.idle/component.idle';

import {
  ApplicationPipe,
  AppMenuComponent,
  FooterComponent,
  HeaderComponent,
  NavContextComponent,
  SelectedNavContextService,
} from './layout';

import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { NoAccessComponent } from './unauthorized/noaccess.component';

import { AppConfig } from './configuration/app.config';
import { ConfigurationService } from './configuration/configuration.service';

import { AuthModule, OidcSecurityService } from 'angular-auth-oidc-client';

import {
  CachingInterceptor,
  DataFormat,
  GlobalErrorHandler,

  LocalStorageModule, LocalStorageService,

  MessageService,

  NotificationComponent, NotificationService,

  PipeModule,
  PreviewComponent,
  PreviewService,
  RequestCache,
  RequestInterceptor,
  ScheduleService,

  SessionStorageModule, SessionStorageService,
} from './common';

import CustomUriEncoder from './common/custom.uri.encoder';

import { NgxEditorModule } from '../externals/ngx-editor/src/ngx-editor.module';

import {

  BulletinHistoryService, BulletinMachineService, BulletinParticipantService, BulletinService,
  CustomPropTypeService, CustomPropValueService,
  CaseCategoryService, CaseHistoryService, CaseMachineService, CaseParticipantService, CaseService,

  ClarificationRequestHistoryService, ClarificationRequestMachineService, ClarificationRequestService,

  DataEntryAttachmentService, DataEntryManifestService, DataEntryService, DataEntryRenderService, ManualCreateService,
  InspectionService, InspectionEntryService,

  FaqMessageService,
  FaqModalComponent,
  FormService,
  HelpMessageService,
  IdentityService,
  IndicatorComponent,
  InternalMessageService,
  KPIService,

  LabelService,
  SecGroupService, SecGroupRelationService,

  NotificationManagementService,

  MaintenanceMessageFetchService, MaintenanceMessageModalComponent, MaintenanceMessageModalService, MaintenanceMessageService,

  ModificationRequestHistoryService, ModificationRequestMachineService, ModificationRequestService,

  OfficialActService, OfficialActContentService, OfficialActMachineService,
  OrganizationService,

  PortalMessageService,
  ProfileService,
  RoleService,

  WorkflowTemplateManifestService, WorkflowTemplateService,

  HomeComponent,
  DashboardContainerComponent, DashboardReportComponent, DashboardDataEntryManualCreateComponent, DashboardService, DashboardKpiComponent, WorkflowInstanceService,
  ReportComponent,

  UniversalService, UniversalPipe
} from './modules';
import { ExcelService } from './common/excel.service';
import { NgxSpinnerModule } from "ngx-spinner";
import { TranslationService } from './services/translation.service';
import { SignalRService } from './services/signalr.service';
import { CodeListService } from './modules/codelist/codelist.service';
import { CodeListItemService } from './modules/codelist.item/codelist.item.service';
import { UniversalResolver } from './common/dynamic-form/universal/universal-resolver';
import { FilterPipe } from './common/filter.pipe';

const appInitializerFn = (appConfig: ConfigurationService) => {
  return () => {
    return appConfig.loadConfig();
  };
};

@NgModule({
  declarations: [
    AppComponent,
    AppMenuComponent,
    FooterComponent,
    HeaderComponent,
    IdleComponent,
    IndicatorComponent,
    MaintenanceMessageModalComponent,
    NavContextComponent,
    NoAccessComponent,
    NotificationComponent,
    PreviewComponent,
    UnauthorizedComponent,
    FaqModalComponent,

    HomeComponent, ReportComponent,
    DashboardContainerComponent, DashboardReportComponent, DashboardKpiComponent, DashboardDataEntryManualCreateComponent,

    UniversalPipe, ApplicationPipe
  ],
  imports: [
    AppRoutingModule,
    AuthModule.forRoot(),
    BrowserAnimationsModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    FormsModule,
    HttpClientModule,
    LocalStorageModule.withConfig({ prefix: 'mekheiasia' }),
    ModalModule.forRoot(),
    NgxEditorModule,
    PipeModule.forRoot(),
    SessionStorageModule.withConfig({ prefix: 'mekheiasia' }),
    NgxSpinnerModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [ConfigurationService]
    },

    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: 'ORIGIN_URL', useFactory: getBaseUrl },

    AppConfig,
    AuthService,
    ClientLogService,
    TranslationService,
    CanActivateViaAuthGuard,
    ExcelService,
    BulletinHistoryService,
    BulletinMachineService,
    BulletinParticipantService,
    BulletinService,
    CaseCategoryService,
    CaseHistoryService,
    CaseMachineService,
    CaseParticipantService,
    CaseService,
    ClarificationRequestHistoryService,
    ClarificationRequestMachineService,
    ClarificationRequestService,
    ConfigurationService,
    CodeListService,
    CodeListItemService,
    CustomUriEncoder,
    CustomPropTypeService,
    CustomPropValueService,
    DataEntryAttachmentService,
    DataEntryManifestService,
    DataEntryService,
    DataEntryRenderService,
    ManualCreateService,
    DashboardService,
    InspectionService,
    InspectionEntryService,
    DataFormat,
    FaqMessageService,
    FormService,
    HelpMessageService,
    IdentityService,
    InternalMessageService,
    KPIService,
    LabelService,
    SecGroupService,
    SecGroupRelationService,
    LocalStorageService,
    MaintenanceMessageFetchService,
    MaintenanceMessageModalService,
    MaintenanceMessageService,
    MessageService,
    ModificationRequestHistoryService,
    ModificationRequestMachineService,
    ModificationRequestService,
    NotificationManagementService,
    NotificationService,
    OidcSecurityService,
    OfficialActService,
    OfficialActContentService,
    OfficialActMachineService,
    OrganizationService,
    PortalMessageService,
    PreviewService,
    ProfileService,
    RequestCache,
    RoleService,
    ScheduleService,
    SelectedNavContextService,
    SessionStorageService,
    WorkflowTemplateManifestService,
    WorkflowTemplateService,
    WorkflowInstanceService,

    UniversalResolver,
    UniversalService,

    SignalRService
  ],
  entryComponents: [
    //FormAttachmentsComponent
  ],
  bootstrap: [AppComponent],
  exports: [RouterModule]
})
export class AppModule { }

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts